// Court titles come from BE with inconsistent casing and spacing ("SC4", "SC 4", " sc 4"), so every
// comparison goes through the same key: lowercase with all whitespace removed.
export const normalizeCourtTitle = (courtTitle?: string | null): string =>
  courtTitle ? courtTitle.toLowerCase().replace(/\s+/g, '') : '';

// "cc", "incc", and "s" / "gs" / "sc" / "in" with an optional court number ("gs2", "in12").
const FEATURED_COURT_REGEX = /^(?:cc|incc|(?:s|gs|sc|in)(?:\d.*)?)$/;

export const isFeaturedCourt = (courtTitle?: string | null): boolean =>
  FEATURED_COURT_REGEX.test(normalizeCourtTitle(courtTitle));

// INCC is labelled CC; every other court keeps the title BE sent.
export const getCourtLabel = (courtTitle?: string | null): string => {
  if (!courtTitle) {
    return '';
  }

  return normalizeCourtTitle(courtTitle) === 'incc' ? 'CC' : courtTitle.trim();
};

// Label for match cards that only show featured courts; empty string for any other court.
export const getFeaturedCourtLabel = (courtTitle?: string | null): string =>
  isFeaturedCourt(courtTitle) ? getCourtLabel(courtTitle) : '';

// Numbered courts that get a priority slot: 1-32 for each prefix.
const COURT_NUMBER_MIN = 1;
const COURT_NUMBER_MAX = 32;

const COURT_NUMBERS = Array.from(
  { length: COURT_NUMBER_MAX - COURT_NUMBER_MIN + 1 },
  (_, index) => COURT_NUMBER_MIN + index
);

const NUMBERED_COURT_PREFIXES = ['s', 'gs', 'sc', 'in'];

// Order in which a match that goes live is slotted among the other live matches on the ticker:
// cc, incc, s, gs, sc, in, then s1, gs1, sc1, in1 ... s32, gs32, sc32, in32.
// Keys are normalized, so look them up through getCourtPriorityIndex ("GS 3" -> "gs3").
export const COURT_PRIORITY: readonly string[] = [
  'cc',
  'incc',
  ...NUMBERED_COURT_PREFIXES,
  ...COURT_NUMBERS.flatMap((courtNumber) =>
    NUMBERED_COURT_PREFIXES.map((prefix) => `${prefix}${courtNumber}`)
  ),
];

// -1 when the court is not in COURT_PRIORITY (or there is no court).
export const getCourtPriorityIndex = (courtTitle?: string | null): number =>
  COURT_PRIORITY.indexOf(normalizeCourtTitle(courtTitle));
